import React, { useState, useRef } from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import useTranslation from 'hooks/use-translation';

import { Divider, SubTotal, AdditionalCharges } from 'shared/components/receipt';
import useCart from 'hooks/use-cart';
import useColors from 'hooks/use-colors';
import CaliforniaWarning from 'src/checkout/components/confirmation/components/california-warning';
import ChevronIcon from 'assets/chevron-icon';
import { useCheckoutLineItems } from 'checkout/hooks/use-checkout-line-items';

export default function OrderItems(props) {
  const { dispensary, excludePaymentFee } = props;
  const cart = useCart();
  const costBreakdown = useObserver(() => cart.costBreakdown);
  const paymentMethod = useObserver(() => cart.paymentMethod);
  const colors = useColors();
  const { t } = useTranslation();

  const isCaliforniaDispensary = _.includes(['ca', 'california'], dispensary?.location?.state?.toLowerCase() || '');
  const { receipt } = costBreakdown;

  // Collapsible state
  const [open, setOpen] = useState(false);
  const detailsRef = useRef(null);

  // For accessibility: id for aria-controls
  const detailsId = 'order-items-details';

  const displayTotal = excludePaymentFee ? costBreakdown.total - costBreakdown.paymentFee / 100 : costBreakdown.total;

  const checkoutLineItems = useCheckoutLineItems();

  return (
    <Container>
      <Header
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setOpen((o) => !o);
          }
        }}
        type='button'
      >
        <Summary>
          Order summary
          <ControlIcon width={20} height={20} $open={open} />
        </Summary>
        <span>${displayTotal.toFixed(2)}</span>
      </Header>
      <StyledDivider $open={open} />
      <Details id={detailsId} ref={detailsRef} $open={open} aria-hidden={!open}>
        <SubTotal order={costBreakdown} fontSize='20px' color={colors.grey[45]} />{' '}
        <AdditionalCharges
          order={{
            taxAmount: costBreakdown.tax,
            cannabisTax: costBreakdown.cannabisTax,
            paymentFee: costBreakdown.paymentFee,
            salesTaxExistence: costBreakdown.salesTaxExistence,
            cannabisTaxExistence: costBreakdown.cannabisTaxExistence,
            bottleDepositTaxExistence: costBreakdown.bottleDepositTaxExistence,
            paymentMethod,
            checkoutLineItems,
            receipt,
          }}
          fontSize='20px'
          color={colors.grey[45]}
          taxCopy={t('checkout.salesTax', 'Sales Tax:')}
          excludePaymentFee={excludePaymentFee}
        />
        {isCaliforniaDispensary && <CaliforniaWarning isKiosk />}
      </Details>
    </Container>
  );
}

const Container = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    --details-easing: cubic-bezier(0.4, 0, 0.2, 1);

    display: flex;
    flex-direction: column;
    padding: ${spaces[9]};
    background-color: ${colors.white};
    height: 100%;

    & > * {
      flex-grow: 0;
    }
  `}
`;

const Header = styled.button`
  all: unset;
  ${({ theme: { colors } }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    font-size: 24px;
    line-height: 20px;
    font-weight: 600;
    color: ${colors.primaryBlack};
    margin-bottom: 0;
    width: 100%;
    cursor: pointer;
    background: none;
    border: none;
    text-align: left;
    outline: none;
  `}
`;

const Summary = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spaces[4]};
`;

const ControlIcon = styled(ChevronIcon)`
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s var(--details-easing);
`;

const StyledDivider = styled(Divider)`
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  margin-block: ${({ theme, $open }) => ($open ? theme.spaces[6] : 0)};
  transition: margin-block 0.3s var(--details-easing);
`;

const Details = styled.div`
  ${({ theme: { spaces }, $open }) => css`
    overflow: hidden;
    max-height: ${$open ? '500px' : '0'};
    will-change: max-height;
    padding-top: ${$open ? spaces[4] : '0'};
    opacity: ${$open ? 1 : 0};
    transition-property: max-height, opacity, padding-top;
    transition-duration: 0.3s;
    transition-timing-function: var(--details-easing);
  `}
`;
